import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, Shield, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/Skeleton";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const SORT_OPTIONS = [
    { value: "recent_signup", label: "Newest signups" },
    { value: "recent_login", label: "Recently active" },
    { value: "most_sessions", label: "Most sessions" },
    { value: "most_hours", label: "Most hours" },
    { value: "email_asc", label: "Email A→Z" },
];

const PAGE_SIZE = 20;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(new Date(iso), "d MMM yyyy"); } catch { return iso; }
};
const fmtRel = (iso) => {
    if (!iso) return "Never";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
};

export default function AdminUsersPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    useDocumentTitle("Admin · Users");

    const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
    const [debouncedSearch, setDebouncedSearch] = useState(searchInput);
    const role = searchParams.get("role") || "all";
    const sort = searchParams.get("sort") || "recent_signup";
    const page = parseInt(searchParams.get("page") || "1", 10);

    // Debounce search input → URL update
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (debouncedSearch) next.set("search", debouncedSearch); else next.delete("search");
        // Reset to page 1 when search changes
        if ((searchParams.get("search") || "") !== debouncedSearch) next.set("page", "1");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value && value !== "all") next.set(key, value); else next.delete(key);
        if (key !== "page") next.set("page", "1");
        setSearchParams(next, { replace: true });
    };

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setData(null);
        const params = { page, page_size: PAGE_SIZE, sort };
        if (debouncedSearch) params.search = debouncedSearch;
        if (role && role !== "all") params.role = role;
        api.get("/admin/users", { params })
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load users"); });
        return () => { cancelled = true; };
    }, [debouncedSearch, role, sort, page]);

    const showingFrom = data && data.total > 0 ? (data.page - 1) * data.page_size + 1 : 0;
    const showingTo = data ? Math.min(data.page * data.page_size, data.total) : 0;

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in-up" data-testid="admin-users-page">
                <header className="space-y-1.5">
                    <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Shield className="h-3.5 w-3.5" />
                        Admin
                    </p>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                        Users
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Browse every account, filter by role, and dive into their practice + learn activity.
                    </p>
                </header>

                {/* Filters */}
                <Card className="border-border/80 shadow-sm sticky top-16 z-10 backdrop-blur supports-[backdrop-filter]:bg-background/80" data-testid="admin-users-filters">
                    <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                        <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by email or name"
                                className="pl-9"
                                data-testid="admin-users-search-input"
                            />
                        </div>
                        <Select value={role} onValueChange={(v) => updateParam("role", v)}>
                            <SelectTrigger className="w-full sm:w-40" data-testid="admin-users-role-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All roles</SelectItem>
                                <SelectItem value="student">Student</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
                            <SelectTrigger className="w-full sm:w-52" data-testid="admin-users-sort-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                {error && (
                    <Card className="border-destructive/50 bg-destructive/5">
                        <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
                    </Card>
                )}

                {/* Table */}
                <Card className="border-border/80 shadow-sm overflow-hidden" data-testid="admin-users-table-card">
                    {!data ? (
                        <CardContent className="p-0">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Skeleton key={i} className="m-3 h-14 rounded-lg" />
                            ))}
                        </CardContent>
                    ) : data.items.length === 0 ? (
                        <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="admin-users-empty">
                            No users match these filters.
                        </CardContent>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-users-table">
                                <thead className="bg-secondary/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3 text-left">User</th>
                                        <th className="px-4 py-3 text-left hidden md:table-cell">Role</th>
                                        <th className="px-4 py-3 text-left hidden lg:table-cell">Joined</th>
                                        <th className="px-4 py-3 text-left hidden md:table-cell">Last login</th>
                                        <th className="px-4 py-3 text-right">Sessions</th>
                                        <th className="px-4 py-3 text-right hidden sm:table-cell">Hours</th>
                                        <th className="px-4 py-3 text-right hidden md:table-cell">Avg</th>
                                        <th className="px-4 py-3 text-right hidden lg:table-cell">Learn</th>
                                        <th className="px-4 py-3" aria-hidden />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border/70">
                                    {data.items.map((u) => (
                                        <tr
                                            key={u.id}
                                            className="hover:bg-secondary/40 transition-colors cursor-pointer"
                                            data-testid={`admin-users-row-${u.id}`}
                                            onClick={() => { window.location.href = `/admin/users/${u.id}`; }}
                                        >
                                            <td className="px-4 py-3">
                                                <Link to={`/admin/users/${u.id}`} className="flex items-center gap-3 group" onClick={(e) => e.stopPropagation()}>
                                                    <InitialsAvatar name={u.full_name} className="h-8 w-8 text-xs" dataTestId={`admin-users-avatar-${u.id}`} />
                                                    <div className="min-w-0">
                                                        <p className="truncate font-medium text-foreground group-hover:text-primary transition-colors">
                                                            {u.full_name || "—"}
                                                        </p>
                                                        <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                                                    </div>
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell">
                                                <span
                                                    className={cn(
                                                        "rounded-full border px-2 py-0.5 text-xs font-medium",
                                                        u.role === "admin"
                                                            ? "border-primary/40 bg-primary/10 text-primary"
                                                            : "border-border bg-secondary text-muted-foreground"
                                                    )}
                                                    data-testid={`admin-users-role-${u.id}`}
                                                >
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                                                {fmtDate(u.created_at)}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground hidden md:table-cell" data-testid={`admin-users-last-login-${u.id}`}>
                                                {fmtRel(u.last_login_at)}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums" data-testid={`admin-users-sessions-${u.id}`}>
                                                {u.total_sessions}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums hidden sm:table-cell" data-testid={`admin-users-hours-${u.id}`}>
                                                {u.total_hours_practiced.toFixed(1)}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">
                                                {u.average_score == null
                                                    ? <span className="text-muted-foreground">—</span>
                                                    : Math.round(u.average_score)}
                                            </td>
                                            <td className="px-4 py-3 text-right tabular-nums text-muted-foreground hidden lg:table-cell">
                                                {u.chapters_completed}/{u.total_chapters}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                {/* Pagination */}
                {data && data.total > 0 && (
                    <div className="flex items-center justify-between gap-3" data-testid="admin-users-pagination">
                        <p className="text-xs text-muted-foreground tabular-nums">
                            Showing <span className="font-medium text-foreground">{showingFrom}–{showingTo}</span> of{" "}
                            <span className="font-medium text-foreground">{data.total}</span>
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => updateParam("page", String(page - 1))}
                                data-testid="admin-users-prev"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Prev
                            </Button>
                            <span className="text-xs tabular-nums text-muted-foreground">
                                Page {data.page} of {data.total_pages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page >= data.total_pages}
                                onClick={() => updateParam("page", String(page + 1))}
                                data-testid="admin-users-next"
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
